import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAcceptInvite } from '../../api/hooks/useSharing.ts';
import {
  AuthCard,
  buttonClass,
  Field,
  FormError,
  inputClass,
} from '../../components/AuthCard.tsx';

/**
 * Invite acceptance: existing users just accept; new users pick a name and password
 * (the invite email proves address ownership, so no separate verification step).
 */
export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const accept = useAcceptInvite();
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  // Try the bare accept first (works for existing accounts); show the account form
  // when the server says a name/password is needed.
  const [needsAccount, setNeedsAccount] = useState(false);

  function submit(withAccount: boolean) {
    accept.mutate(
      withAccount ? { token, display_name: displayName, password } : { token },
      {
        onSuccess: () => void navigate('/plans', { replace: true }),
        onError: (error) => {
          if (!withAccount && error.message.toLowerCase().includes('display name')) {
            setNeedsAccount(true);
          }
        },
      },
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    submit(true);
  }

  if (!token) {
    return (
      <AuthCard title="Invitation">
        <FormError message="Missing invitation token." />
      </AuthCard>
    );
  }

  if (!needsAccount) {
    return (
      <AuthCard title="You've been invited to view a plan">
        <FormError
          message={
            accept.isError && !accept.error.message.toLowerCase().includes('display name')
              ? 'This invitation is invalid or has expired.'
              : undefined
          }
        />
        <button
          className={buttonClass}
          disabled={accept.isPending}
          onClick={() => submit(false)}
        >
          {accept.isPending ? 'Accepting…' : 'Accept invitation'}
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Create your viewer account">
      <form onSubmit={onSubmit}>
        <FormError
          message={
            accept.isError && !accept.error.message.toLowerCase().includes('display name')
              ? accept.error.message
              : undefined
          }
        />
        <Field label="Your name">
          <input
            className={inputClass}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
            required
          />
        </Field>
        <Field label="Choose a password (10+ characters)">
          <input
            className={inputClass}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={10}
            required
          />
        </Field>
        <button className={buttonClass} type="submit" disabled={accept.isPending}>
          {accept.isPending ? 'Creating…' : 'Accept & view the plan'}
        </button>
      </form>
    </AuthCard>
  );
}
